import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, CircularProgress,
  Table, TableHead, TableBody, TableFooter, TableRow, TableCell, TableContainer, Paper, Alert, Tooltip,
} from '@mui/material';
import ModuleHeader from './components/ModuleHeader';
import EventNoteIcon from '@mui/icons-material/EventNote';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { formatarSE } from './utils/semanaEpi';
import { API_URL } from './config';
import BotaoArquivar from './BotaoArquivar';
import { useArquivoAutomatico } from './ArquivoAutomatico';
import { montarCopiaFiel, PedidoArquivo } from './arquivarCopia';

// Trunca para 1 casa decimal SEM arredondar (20.66 → 20.6, igual ao boletim em papel)
const truncar1 = (n: number): string => {
  if (!isFinite(n) || isNaN(n)) return '0.0';
  return (Math.floor(n * 10) / 10).toFixed(1);
};

interface Ciclo { id: number; nome: string; ativo: boolean; }
interface SeDisponivel { se: number; data_inicio: string; data_fim: string; }

interface SemanaInfo {
  se: number; ano: number;
  seg: string; ter: string; qua: string; qui: string; sex: string;
}

interface DiasNums { seg: number; ter: number; qua: number; qui: number; sex: number; }
interface DiasStrs { seg: string; ter: string; qua: string; qui: string; sex: string; }
interface DiasBools { seg: boolean; ter: boolean; qua: boolean; qui: boolean; sex: boolean; }

interface AgenteRow {
  agente_id: number;
  nome: string;
  tipo_servidor: string;
  dias: DiasNums;
  ponto: DiasStrs;
  manual: DiasBools;
  total_semana: number;
  hd_trabalhado: number;
  rendimento: number;
  // LIRAa deduzido pelo motor (agente na equipe do LIRAa, dia sem boletim) — nao gravado
  ponto_automatico?: Partial<Record<keyof DiasStrs, boolean>>;
  // bloco N6: ciclo que gerou o automatico (so ciclo com data final)
  ponto_automatico_ciclo?: Partial<Record<keyof DiasStrs, { nome: string; data_fim: string }>>;
}

const dicaLiraaAuto = (c?: { nome: string; data_fim: string }) =>
  c ? `Preenchido pelo LIRAa (ciclo ${c.nome}, encerrado em ${c.data_fim.split('-').reverse().join('/')}). Para mudar, escolha outro código.`
    : 'Preenchido pelo LIRAa. Para mudar, escolha outro código.';

interface Totais {
  por_dia: DiasNums;
  geral: {
    total: number;
    agentes_com_producao: number;
    media_por_agente: number;
  };
}

interface Fechamento {
  fechado: boolean;
  data_fechamento: string;
  supervisor: string | null;
}

interface ObsItem {
  agente: string;
  data: string;
  texto: string;
}

interface Producao {
  semana: SemanaInfo;
  agentes: AgenteRow[];
  totais: Totais;
  fechamento: Fechamento | null;
  observacoes: ObsItem[];
}

// Célula de produção clicável — vira input ao clicar, salva ao sair
const CelulaProducao: React.FC<{
  valor: number;
  isManual: boolean;
  agenteId: number;
  dataISO: string;
  podeEditar: boolean;
  onSalvar: (agenteId: number, dataISO: string, valor: number) => Promise<void>;
}> = ({ valor, isManual, podeEditar, agenteId, dataISO, onSalvar }) => {
  const [editando, setEditando] = useState(false);
  const [tmp, setTmp] = useState(String(valor));

  if (!podeEditar) return <span>—</span>;

  if (editando) {
    return (
      <input
        type="number"
        value={tmp}
        autoFocus
        onChange={e => setTmp(e.target.value)}
        onBlur={async () => {
          setEditando(false);
          const novo = parseInt(tmp, 10);
          if (!isNaN(novo) && novo !== valor) {
            await onSalvar(agenteId, dataISO, novo);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') { setTmp(String(valor)); setEditando(false); }
        }}
        style={{
          width: 50, textAlign: 'center', border: '1px solid #FF9500',
          borderRadius: 4, padding: '2px 4px', fontSize: '0.85rem',
        }}
      />
    );
  }

  return (
    <span
      onClick={() => { setTmp(String(valor)); setEditando(true); }}
      title="Clique para editar a produção"
      style={{
        cursor: 'pointer',
        color: isManual ? '#FF9500' : 'inherit',
        fontWeight: isManual ? 700 : 'inherit',
        padding: '2px 6px',
        borderRadius: 4,
        display: 'inline-block',
      }}
    >
      {valor === 0 ? '—' : valor}{isManual ? ' *' : ''}
    </span>
  );
};

export default function BoletimPonto({ onVoltar }: { onVoltar: () => void }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [ciclos,   setCiclos]   = useState<Ciclo[]>([]);
  const [cicloId,  setCicloId]  = useState<number | null>(null);
  const [semanas,  setSemanas]  = useState<SeDisponivel[]>([]);
  const [se,       setSe]       = useState<number | null>(null);
  const [producao, setProducao] = useState<Producao | null>(null);
  const [loading,  setLoading]  = useState(false);

  // Arquivos: botão "Arquivar" + arquivamento automático depois de "Fechar Ponto" (Bloco 8)
  const { arquivar: arquivarAuto, avisoNaTela: avisoArquivo } = useArquivoAutomatico('O ponto da semana fechou');
  const [arquivarAposFechar, setArquivarAposFechar] = useState<{ ciclo: string; se: string } | null>(null);
  const pedidoArquivo = useMemo<PedidoArquivo | null>(() => {
    if (!cicloId || !se || !producao) return null;
    const nomeCiclo = ciclos.find(c => String(c.id) === String(cicloId))?.nome ?? `Ciclo ${cicloId}`;
    const sem = semanas.find(s => s.se === se);
    const dm = (d: string) => d.split('-').reverse().slice(0, 2).join('/');
    return {
      pasta: 'boletim_ponto', tipo: 'boletim_ponto', chave: `boletim_ponto|c${cicloId}|se${se}`,
      titulo: `Boletim de Ponto · SE ${se} · ${nomeCiclo}`, ciclo_id: Number(cicloId), se: Number(se),
      referencia: sem ? `SE ${se} (${dm(sem.data_inicio)} – ${dm(sem.data_fim)})` : `SE ${se}`,
      ano: producao.semana?.ano ?? null,
      avisos: producao.fechamento ? [] : ['Semana ainda aberta: o ponto pode mudar depois de arquivado.'],
    };
  }, [cicloId, se, producao, ciclos, semanas]);
  const opcoesPonto = useMemo(() => ({ cabecalho: pedidoArquivo?.titulo, paisagem: true }), [pedidoArquivo]);
  useEffect(() => {
    if (!arquivarAposFechar || !pedidoArquivo || !producao?.fechamento) return;
    if (String(arquivarAposFechar.ciclo) !== String(cicloId) || String(arquivarAposFechar.se) !== String(se)) { setArquivarAposFechar(null); return; }
    setArquivarAposFechar(null);
    const el = document.getElementById('bp-papel');
    const foto = el ? montarCopiaFiel(el, pedidoArquivo.titulo, opcoesPonto) : Promise.reject(new Error('A grade da semana não estava na tela.'));
    foto.catch(() => undefined);
    void arquivarAuto([{ pedido: { ...pedidoArquivo, avisos: [], origem: 'automatico', observacao: 'Arquivado automaticamente ao fechar o ponto da semana.' }, html: () => foto }]);
  }, [arquivarAposFechar, pedidoArquivo, producao, cicloId, se, opcoesPonto, arquivarAuto]);

  // Carrega ciclos na montagem
  useEffect(() => {
    axios.get(`${API_URL}/api/ciclos/`, { headers }).then(r => {
      setCiclos(r.data);
      const ativo = r.data.find((c: Ciclo) => c.ativo);
      if (ativo) setCicloId(ativo.id);
    }).catch(() => {});
  }, []);

  // Carrega semanas disponíveis quando ciclo muda
  useEffect(() => {
    const params = cicloId ? { ciclo_id: cicloId } : {};
    axios.get(`${API_URL}/api/boletim-ponto/semanas-disponiveis`, { headers, params }).then(r => {
      setSemanas(r.data);
      if (r.data.length > 0) setSe(r.data[r.data.length - 1].se);
      else setSe(null);
    }).catch(() => setSemanas([]));
  }, [cicloId]);

  const fetchProducao = useCallback(async () => {
    if (!se) return;
    setLoading(true);
    try {
      const params: Record<string, number> = { se };
      if (cicloId) params.ciclo_id = cicloId;
      const r = await axios.get(`${API_URL}/api/boletim-ponto/producao`, { headers, params });
      setProducao(r.data);
    } catch {
      setProducao(null);
    } finally {
      setLoading(false);
    }
  }, [se, cicloId, token]);

  const handleAjustarProducao = useCallback(async (agenteId: number, dataISO: string, valor: number) => {
    try {
      await axios.post(
        `${API_URL}/api/boletim-ponto/ajustar-producao`,
        { agente_id: agenteId, data: dataISO, producao_nova: valor },
        { headers }
      );
      await fetchProducao();
    } catch {
      alert('Erro ao ajustar produção');
    }
  }, [fetchProducao, token]);

  const fmtData = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  const fecharPonto = async () => {
    if (!window.confirm('Deseja fechar o ponto desta semana?')) return;
    try {
      await axios.post(
        `${API_URL}/api/boletim-ponto/fechar-semana`,
        { se, ciclo_id: cicloId },
        { headers }
      );
      alert('Ponto fechado com sucesso');
      fetchProducao();
      setArquivarAposFechar({ ciclo: String(cicloId), se: String(se) });
    } catch (e: unknown) {
      const msg = axios.isAxiosError(e) ? (e.response?.data?.detail ?? e.message) : 'Erro ao fechar ponto';
      alert(msg);
    }
  };

  const reabrirPonto = async () => {
    if (!window.confirm('Deseja reabrir o ponto desta semana para edição?')) return;
    try {
      await axios.post(
        `${API_URL}/api/boletim-ponto/reabrir-semana`,
        { se, ciclo_id: cicloId },
        { headers }
      );
      fetchProducao();
    } catch (e: unknown) {
      const msg = axios.isAxiosError(e) ? (e.response?.data?.detail ?? e.message) : 'Erro ao reabrir ponto';
      alert(msg);
    }
  };

  const corPonto = (codigo: string): string => {
    switch (codigo) {
      case 'X':     return '#34C759';
      case 'PEs':   return '#6B5BFF';
      case 'LIRAa': return '#0E7490';
      case 'Lab':   return '#FF9F0A';
      case 'F':     return '#FF3B30';
      case 'L':     return '#FF9500';
      case 'Fe':    return '#0071E3';
      case 'S':     return '#5856D6';
      case 'FJ':    return '#FFCC00';
      case 'O':     return '#8E8E93';
      case 'Liber': return '#7B5FE0';
      case 'PF':    return '#AF52DE';
      case 'H':     return '#5AC8FA';
      default:      return '#8E8E93';
    }
  };

  const codigosParaAgente = (tipoServidor?: string): string[] => {
    const ausencias = ['F', 'L', 'Fe', 'S', 'FJ', 'O', 'Liber', 'PF', 'H'];
    if (tipoServidor === 'laboratorio') {
      return ['Lab', 'LIRAa', ...ausencias];
    }
    return ['X', 'PEs', 'LIRAa', ...ausencias];
  };
  const DIAS_CHAVE = ['seg', 'ter', 'qua', 'qui', 'sex'] as const;

  const salvarMarcacao = useCallback(async (agente_id: number, data: string, codigo: string) => {
    const pontoAnterior = producao?.agentes.find(a => a.agente_id === agente_id)?.ponto;

    setProducao(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        agentes: prev.agentes.map(a =>
          a.agente_id === agente_id
            ? { ...a, ponto: { ...a.ponto, ...Object.fromEntries(
                DIAS_CHAVE.filter(c => prev.semana[c] === data).map(c => [c, codigo])
              )}, ponto_automatico: { ...a.ponto_automatico, ...Object.fromEntries(
                DIAS_CHAVE.filter(c => prev.semana[c] === data).map(c => [c, false])
              )}}
            : a
        ),
      };
    });

    try {
      await axios.post(
        `${API_URL}/api/boletim-ponto/marcacao`,
        { agente_id, data, codigo },
        { headers }
      );
    } catch (e: unknown) {
      // Reverte o estado otimista em caso de erro
      if (pontoAnterior) {
        setProducao(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            agentes: prev.agentes.map(a =>
              a.agente_id === agente_id ? { ...a, ponto: pontoAnterior } : a
            ),
          };
        });
      }
      const msg = axios.isAxiosError(e) ? (e.response?.data?.detail ?? e.message) : 'Erro ao salvar marcação';
      alert(msg);
    }
  }, [producao, token]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
      <ModuleHeader
        titulo="Boletim de Ponto"
        subtitulo="Frequência semanal por agente"
        icone={<EventNoteIcon sx={{ fontSize: 32 }} />}
        accentColor="#6A1B9A"
        onVoltar={onVoltar}
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select label="Ciclo" size="small" sx={{ minWidth: 180 }}
            value={cicloId ?? ''}
            onChange={e => setCicloId(e.target.value ? Number(e.target.value) : null)}
          >
            <MenuItem value="">Todos os ciclos</MenuItem>
            {ciclos.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
          </TextField>

          <TextField
            select label="Semana Epidemiológica" size="small" sx={{ minWidth: 260 }}
            value={se ?? ''}
            onChange={e => setSe(e.target.value ? Number(e.target.value) : null)}
          >
            {semanas.length === 0 && <MenuItem value="">Nenhuma SE disponível</MenuItem>}
            {semanas.map(s => (
              <MenuItem key={s.se} value={s.se}>
                SE {s.se} — {fmtData(s.data_inicio)} a {fmtData(s.data_fim)}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained" onClick={fetchProducao}
            disabled={!se || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Atualizar
          </Button>

          {producao?.fechamento?.fechado === true ? (
            <Button
              variant="contained" color="secondary"
              onClick={reabrirPonto}
              disabled={!se || !cicloId}
            >
              Reabrir Ponto
            </Button>
          ) : (
            <Button
              variant="contained" color="warning"
              onClick={fecharPonto}
              disabled={!se || !cicloId}
            >
              Fechar Ponto
            </Button>
          )}
          <BotaoArquivar alvo={() => document.getElementById('bp-papel')} pedido={pedidoArquivo} opcoes={opcoesPonto} />
        </Box>

        {se && (
          <Typography sx={{ mb: 2, fontWeight: 600, fontSize: 14, color: '#1D1D1F' }}>
            {formatarSE(se)}
          </Typography>
        )}

        {producao && (() => {
          const s = producao.semana;
          const fmt = (d: string) =>
            new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          const dayBorder = { borderLeft: '2px solid #D2D2D7' };
          const thSx = {
            color: '#ffffff', fontWeight: 700, fontSize: 11,
            textTransform: 'uppercase' as const, letterSpacing: 0.5,
            whiteSpace: 'nowrap' as const, px: 1,
          };
          const fech = producao.fechamento;
          return (
            <>
            <Box id="bp-papel">
            {fech?.fechado && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Ponto fechado em{' '}
                {new Date(fech.data_fechamento + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                {fech.supervisor ? ` por ${fech.supervisor}` : ''}
              </Alert>
            )}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>Presença:</strong> X = Trabalhou (campo) · PEs = Ponto Estratégico · Lab = Laboratório (Adriana) · <span style={{ color: '#0E7490', fontWeight: 700 }}>LIRAa</span> = dia de LIRAa (presença, não conta produção). Preenchido sozinho quando o ciclo recebe data final.
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>Ausências justificadas:</strong> L = Licença · Fe = Férias · S = Suspenso · FJ = Falta Justificada · Liber = Liberado pela gestão · PF = Ponto Facultativo · H = Feriado
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>Ausências não justificadas:</strong> F = Falta · O = Falta sem Justificar
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>Produção:</strong> clique no número para editar · * = ajuste manual · — = sem produção
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1D1D1F' }}>
                    <TableCell sx={{ ...thSx, minWidth: 160 }}>Nome</TableCell>
                    <TableCell sx={{ ...thSx, ...dayBorder }} align="center">Seg {fmt(s.seg)}</TableCell>
                    <TableCell sx={thSx} align="center">Ponto</TableCell>
                    <TableCell sx={{ ...thSx, ...dayBorder }} align="center">Ter {fmt(s.ter)}</TableCell>
                    <TableCell sx={thSx} align="center">Ponto</TableCell>
                    <TableCell sx={{ ...thSx, ...dayBorder }} align="center">Qua {fmt(s.qua)}</TableCell>
                    <TableCell sx={thSx} align="center">Ponto</TableCell>
                    <TableCell sx={{ ...thSx, ...dayBorder }} align="center">Qui {fmt(s.qui)}</TableCell>
                    <TableCell sx={thSx} align="center">Ponto</TableCell>
                    <TableCell sx={{ ...thSx, ...dayBorder }} align="center">Sex {fmt(s.sex)}</TableCell>
                    <TableCell sx={thSx} align="center">Ponto</TableCell>
                    <TableCell sx={thSx} align="center">Total</TableCell>
                    <TableCell sx={thSx} align="center">H/D</TableCell>
                    <TableCell sx={thSx} align="center">Rend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {producao.agentes.map((a, idx) => (
                    <TableRow key={a.agente_id} sx={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#FBFBFD', '&:hover': { backgroundColor: '#F0F7FF' } }}>
                      <TableCell sx={{ backgroundColor: '#FAFAFA', fontWeight: 500, whiteSpace: 'nowrap' }}>{a.nome}</TableCell>
                      {DIAS_CHAVE.map(dia => (
                        [
                          <TableCell key={`${dia}-num`} align="center" sx={dayBorder}>
                            <CelulaProducao
                              valor={a.dias[dia] || 0}
                              isManual={a.manual?.[dia] || false}
                              agenteId={a.agente_id}
                              dataISO={s[dia]}
                              podeEditar={a.tipo_servidor !== 'laboratorio'}
                              onSalvar={handleAjustarProducao}
                            />
                          </TableCell>,
                          <TableCell key={`${dia}-ponto`} align="center" sx={{ px: 0.5 }}>
                            <Tooltip title={a.ponto_automatico?.[dia] ? dicaLiraaAuto(a.ponto_automatico_ciclo?.[dia]) : ''}>
                            <span style={{ display: 'inline-block' }}>
                            <TextField
                              select size="small"
                              value={a.ponto[dia]}
                              onChange={e => salvarMarcacao(a.agente_id, s[dia], e.target.value)}
                              disabled={producao?.fechamento?.fechado === true}
                              sx={{ minWidth: 64, '& .MuiSelect-select': {
                                color: corPonto(a.ponto[dia]), fontWeight: 700,
                                ...(a.ponto[dia] === 'LIRAa' ? { backgroundColor: '#CFFAFE' } : {}),
                              } }}
                              slotProps={{ select: { displayEmpty: true, renderValue: (v: unknown) => (v ? String(v) : <em>—</em>) } }}
                            >
                              <MenuItem value=""><em>—</em></MenuItem>
                              {codigosParaAgente(a.tipo_servidor).map(c => (
                                <MenuItem key={c} value={c}>
                                  {c === 'LIRAa' ? 'LIRAa — dia de levantamento (presença, sem produção)' : c}
                                </MenuItem>
                              ))}
                            </TextField>
                            </span>
                            </Tooltip>
                          </TableCell>,
                        ]
                      ))}
                      <TableCell align="center" sx={{ backgroundColor: '#E8F4FF', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{a.tipo_servidor === 'laboratorio' ? '—' : a.total_semana}</TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#E8F4FF', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{a.tipo_servidor === 'laboratorio' ? '—' : a.hd_trabalhado}</TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#E8F4FF', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{a.tipo_servidor === 'laboratorio' ? '—' : truncar1(a.rendimento)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ backgroundColor: '#1D1D1F' }}>
                    {(() => {
                      const g = producao.totais.geral;
                      const tfSx = { color: '#fff', fontWeight: 700, fontSize: 12, fontVariantNumeric: 'tabular-nums' as const };
                      return (
                        <>
                          <TableCell sx={{ ...tfSx, whiteSpace: 'nowrap' }}>TOTAL</TableCell>
                          {DIAS_CHAVE.map(dia => [
                            <TableCell key={`${dia}-tot`} align="center" sx={{ ...tfSx, ...dayBorder, borderLeftColor: '#3D3D3F' }}>
                              {producao.totais.por_dia[dia]}
                            </TableCell>,
                            <TableCell key={`${dia}-ativ`} align="center" sx={tfSx}>
                              {producao.agentes.filter(a => a.dias[dia] > 0).length}
                            </TableCell>,
                          ])}
                          <TableCell align="center" sx={tfSx}>{g.total}</TableCell>
                          <TableCell align="center" sx={tfSx}>{producao.agentes.filter(a => a.tipo_servidor !== 'laboratorio').reduce((s, a) => s + (a.hd_trabalhado || 0), 0)}</TableCell>
                          <TableCell align="center" sx={tfSx}>{(() => {
                            const tot = producao.agentes.filter(a => a.tipo_servidor !== 'laboratorio').reduce((s, a) => s + (a.total_semana || 0), 0);
                            const hd  = producao.agentes.filter(a => a.tipo_servidor !== 'laboratorio').reduce((s, a) => s + (a.hd_trabalhado || 0), 0);
                            return hd > 0 ? truncar1(tot / hd) : '0.0';
                          })()}</TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
            </Box>

            {producao.observacoes && producao.observacoes.length > 0 && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#FFF8E1', borderRadius: 2, border: '1px solid #FF9500' }}>
                <Typography sx={{ fontWeight: 700, mb: 1, color: '#1D1D1F', fontSize: '0.9rem' }}>
                  Observações — Ajustes manuais nesta semana
                </Typography>
                {producao.observacoes.map((o, i) => (
                  <Typography key={i} sx={{ fontSize: '0.85rem', color: '#444', mb: 0.5 }}>
                    • {o.texto.split('\n').join(' | ')}
                  </Typography>
                ))}
              </Box>
            )}
            </>
          );
        })()}
      </Box>
      {avisoArquivo}
    </Box>
  );
}
